import Swiper from "swiper";
import { Autoplay, EffectCoverflow, Virtual } from "swiper/modules";
import { SwiperOptions } from "swiper/types/swiper-options";

import { Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";

import {
  getActivityConfig,
  postGetActivityDetails,
} from "@/api/clientApi/activity.ts";
import { Button } from "@/components/Base/Button/Button.tsx";
import { SwiperBase, SwiperSlide } from "@/components/Base/Swiper/BaseSwiper";
import Icon from "@/components/Icon.tsx";
import {
  SessionStorageKey,
  filterLoginTargetsState,
  filterNoLoginTargetsState,
} from "@/constants/constants.ts";
import { getImageAltByLang } from "@/constants/seo-constants";
import { useConfigRestrict } from "@/hooks/useConfigRestrict";
import { getLangKey } from "@/i18n";
import { isMobile } from "@/lib/isMobile.ts";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "@/lib/sessionStorage.ts";
import { cn } from "@/lib/tailwindcssTool.ts";
import { getBuoyListCfg } from "@/stores/configStore.ts";
import { userStore } from "@/stores/userStore.ts";
import {
  ActivityDetailStatus,
  ActivityTypeEnum,
  ClickType,
} from "@/types/compontent/base/baseEnum.ts";
import { ActivityConfig } from "@/types/swaggerex/activity";

interface BuoyItem {
  id: string;
  status: boolean;
  // 前端字段，暂存用户是否点击了关闭按钮，用户刷新时重置浮标显示状态
  manualStatus: boolean;
  login_type_limit: number;
  atype: number;
  click_id?: ClickType;
  click_params?: string;
  icon?: Record<string, string>;
  countdown_status?: boolean;
  countdown_time?: number;
  end_time?: number;
  general_list: [];
  deny_pack_type: number;
  label_list: [];
  label_type_limit: number;
}

const CountdownButton = (props: {
  item: { end_time?: number; countdown_time?: number };
  onCountdownEnd?: () => void;
}) => {
  const getInitialRemainingTime = () => {
    const now = Math.floor(Date.now() / 1000);
    if (props.item?.end_time) {
      return Math.max(0, props.item.end_time - now);
    } else if (props.item.countdown_time) {
      return props.item.countdown_time * 60;
    }
    return 0;
  };

  const [remainingTime, setRemainingTime] = createSignal(
    getInitialRemainingTime(),
  );
  const [displayTime, setDisplayTime] = createSignal("00:00:00");

  createEffect(() => {
    updateDisplayTime();

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          clearInterval(timer);
          props?.onCountdownEnd?.(); // 触发回调
          return 0;
        }
        return newTime;
      });
      updateDisplayTime();
    }, 1000);

    onCleanup(() => clearInterval(timer));
  });

  const updateDisplayTime = () => {
    const time = remainingTime();
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (time % 60).toString().padStart(2, "0");

    if (hours > 0) {
      setDisplayTime(
        `${hours.toString().padStart(2, "0")}h:${minutes}m:${seconds}s`,
      );
    } else {
      setDisplayTime(`${minutes}m:${seconds}s`);
    }
  };

  return (
    <Button
      variant="primary"
      class={cn(
        "mt-1 h-[20px] whitespace-nowrap text-center text-[12px] font-[600]",
        isMobile() ? "w-[95px]" : "w-[135px]",
      )}
    >
      {displayTime()}
    </Button>
  );
};

/**
 * -
 *
 * 设置Swiper虚拟渲染的最小阀值, 不建议设置过小, 不然会导致
 * https://zendao.baidu.date/bug-view-3700.html 类似问题, 最小设置为5(2+2+1)
 */
const NEED_VIRTUAL = 2;

const BuoyComponent = () => {
  const [visible, setVisible] = createSignal(false);
  const [dataList, setDataList] = createSignal<BuoyItem[]>([]);
  const [offsetRight, setOffsetRight] = createSignal(12);
  let scrollTimeout: string | number | NodeJS.Timeout | undefined;
  let swiperContainer: HTMLDivElement | undefined;
  let swiperInstance: Swiper | undefined;
  const [isInitializing, setIsInitializing] = createSignal<boolean>(false);
  let isMounted = true;
  const { getStatusByDeny, getStatusByLabelsDeny, getStatusByPay } =
    useConfigRestrict();
  const swiperConfig: SwiperOptions = {
    modules: [Autoplay, EffectCoverflow, Virtual],
    slidesPerView: 1,
    slidesPerGroup: 1,
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: true,
      pauseOnMouseEnter: true,
    },
    on: {
      slideChange: (swiper) => {
        if (dataList().length > 1) {
          setTimeout(() => {
            swiper?.autoplay?.start();
          }, 250);
        }
      },
    },
  };

  const handleScroll = () => {
    setOffsetRight(isMobile() ? -46 : -64);
    if (swiperInstance && dataList().length > 1)
      swiperInstance?.autoplay?.stop();

    clearTimeout(scrollTimeout);

    scrollTimeout = setTimeout(() => {
      setOffsetRight(12);
      if (swiperInstance && dataList().length > 1)
        swiperInstance?.autoplay?.start();
    }, 250);
  };

  const destroySwiper = () => {
    if (swiperInstance) {
      try {
        if (swiperInstance?.virtual) swiperInstance?.virtual?.removeAllSlides();
        swiperInstance?.autoplay?.stop();
        swiperInstance?.destroy(true, true);
        setIsInitializing(false);
      } catch (error) {
        console.error("Failed to destroy swiper:", error);
      } finally {
        swiperInstance = undefined;
      }
    }
  };

  const initializeSwiper = () => {
    if (!isMounted) return;
    if (swiperContainer && !swiperInstance && !isInitializing()) {
      // 检查 DOM 元素是否仍然存在且已挂载
      if (!swiperContainer.isConnected) return;
      setIsInitializing(true);
      try {
        swiperInstance = new Swiper(swiperContainer, {
          ...swiperConfig,
          virtual: false,
          // dataList().length > NEED_VIRTUAL && isMobile()
          //   ? {
          //       enabled: true,
          //       cache: true,
          //       // addSlidesBefore: 1,
          //       // addSlidesAfter: 1,
          //     }
          //   : false,
        });
      } catch (error) {
        console.error("Failed to initialize swiper:", error);
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    }
  };

  onCleanup(() => {
    isMounted = false;
    destroySwiper();
    clearTimeout(scrollTimeout);
  });

  // TODO: 活动过滤接口抽象化，逻辑不互通
  createEffect(
    // on(
    //   createMemo(() => JSON.stringify(getBuoyListCfg()?.buoyList || [])),
    async () => {
      if (!isMounted) return;
      // setDataList([]);
      let list = getBuoyListCfg()?.buoyList || [];

      if (list.length > 0) {
        destroySwiper();
        if (getSessionStorageItem(SessionStorageKey.Buoy, null) !== null) {
          list = getSessionStorageItem(SessionStorageKey.Buoy, []);
          list = list.map((item: BuoyItem) => {
            // const filterItem = getBuoyListCfg()?.buoyList.filter((innerItem: BuoyItem) => item.id === innerItem.id)?.[0];
            return {
              ...item,
              // // 如果倒计时开启，添加过期时间
              // ...(item.countdown_status && {
              //   end_time:
              //     Math.floor(Date.now() / 1000) + (item.countdown_time || 0) * 60,
              // }),
              // 没有end_time，或者时间还没到期的，重置显示状态
              ...(item.status &&
                item.countdown_status &&
                (!item.end_time ||
                  (item?.end_time || 0) > Math.floor(Date.now() / 1000)) && {
                  manualStatus: true,
                }),
              ...(item.status &&
                !item.countdown_status && {
                  manualStatus: true,
                }),
            };
          });
        } else {
          list = list.map((item: BuoyItem) => {
            return {
              ...item,
              // 如果倒计时开启，添加过期时间
              ...(item.countdown_status && {
                end_time:
                  Math.floor(Date.now() / 1000) +
                  (item.countdown_time || 0) * 60,
              }),
              manualStatus: true,
            };
          });
        }
        // enums=不限制:0,enums=登录可见:1,enums=未登录可见:2
        const filterEnums = userStore.isLogin ? [0, 1, 3] : [0, 2];
        const filterLoginTargets = filterLoginTargetsState;
        const filterNoLoginTargets = filterNoLoginTargetsState;
        // 需要过滤的活动列表
        const limitBase = userStore.isLogin
          ? filterLoginTargets
          : filterNoLoginTargets;
        const filterActivity = limitBase.filter((filterItem) =>
          list.some(
            (listItem: {
              status: boolean;
              atype: ActivityTypeEnum;
              click_params: string;
            }) =>
              listItem.status &&
              listItem.atype === filterItem.atype &&
              listItem.click_params?.includes(filterItem.click_params),
          ),
        );
        const filterAtypes: Record<number, boolean> = {};
        //有需要判断时进入判断
        if (filterActivity.length > 0) {
          if (userStore.isLogin) {
            // 需要过滤活动，请求活动status
            const details = await postGetActivityDetails(
              { atypes: filterActivity.map((item) => item.atype) },
              false,
            );
            const activeStatus: Record<number, boolean> = {
              [ActivityDetailStatus.canReceive]: true,
              [ActivityDetailStatus.canComplete]: true,
              [ActivityDetailStatus.toReceive]: true,
              [ActivityDetailStatus.check]: true,
              [ActivityDetailStatus.countdown]: true,
            };
            const activityDetails = details?.activity_details || {};
            // 循环修改状态
            filterActivity.forEach((filterItem) => {
              const item = activityDetails[filterItem.atype];
              if (item) {
                const newStatus: number = Number(
                  item.details_status?.new_status || 0,
                );
                if (item.activity_status === 1 && activeStatus[newStatus]) {
                  filterItem.status = true;
                } else {
                  filterItem.status = false;
                }
              } else {
                filterItem.status = false;
              }
              if (!filterItem.status) {
                filterAtypes[filterItem.atype] = true;
              }
            });
          } else {
            const activityCfg = await getActivityConfig(
              filterActivity.map((item) => item.atype).join(","),
            );
            const activityMaps: Record<number, ActivityConfig> = {};
            activityCfg.activity_cfg_list?.forEach((item) => {
              activityMaps[item.atype] = item;
            });
            filterActivity.forEach((filterItem) => {
              const item = activityMaps[filterItem.atype];
              if (!item || !item.status) {
                filterItem.status = false;
                filterAtypes[filterItem.atype] = true;
              } else {
                filterItem.status = true;
              }
            });
          }
        }
        const temp = list.filter((item: BuoyItem) => {
          // 过滤需要过滤得活动
          if (filterAtypes[item.atype]) {
            return false;
          }
          // 活动状态
          if (!item?.status || !item?.manualStatus) {
            return false;
          }

          // 倒计时开启时 判断结束时间是否小于当前时间
          if (item?.countdown_status === true) {
            if ((item?.end_time || 0) <= Math.floor(Date.now() / 1000)) {
              return false;
            }
          }
          // 登录限制类型
          const loginTypeEnable = filterEnums.includes(item?.login_type_limit);
          if (!loginTypeEnable) {
            return false;
          }
          // 登录充值判断 未登录浮标状态判断
          if (userStore.isLogin) {
            const loginPayEnable =
              item?.login_type_limit === 3
                ? getStatusByPay(item?.login_type_limit === 3 ? 1 : 2)
                : true;
            if (!loginPayEnable) {
              return false;
            }
          } else {
            const noLoginEnable =
              filterActivity.length !== 0
                ? filterActivity.filter(
                    (m) =>
                      m.click_params === item.click_params &&
                      m.atype === item.atype,
                  )?.[0]?.status
                : true;
            if (!noLoginEnable) {
              return false;
            }
          }
          //总代渠道限制
          const generalEnable = getStatusByDeny(
            item?.general_list,
            item?.deny_pack_type,
            true,
          );
          if (!generalEnable) {
            return false;
          }
          //标签限制
          const labelEnable = getStatusByLabelsDeny(
            item?.label_list,
            item?.label_type_limit,
          );
          if (!labelEnable) {
            return false;
          }
          return true;
        });
        if (!isMounted) return;
        setSessionStorageItem(SessionStorageKey.Buoy, list);
        setDataList(temp);
      } else {
        if (!isMounted) return;
        setDataList([]);
      }
    },
    // ),
  );
  // 时间到期后更新swiper
  // createEffect(
  //   on(
  //     createMemo(() => dataList()),
  //     (list) => {
  //       if (list.length > 0) {
  //         setVisible(true);
  //         // 重新创建 Swiper 实例
  //         requestAnimationFrame(initializeSwiper);
  //       } else {
  //         setVisible(false);
  //       }
  //     },
  //   ),
  // );
  createEffect(() => {
    const list = dataList();
    if (!isMounted) return;
    if (list.length > 0) {
      setVisible(true);
      // 重新创建 Swiper 实例
      requestAnimationFrame(() => {
        if (isMounted) {
          initializeSwiper();
        }
      });
    } else {
      destroySwiper();
      setVisible(false);
    }
  });

  // 时间到期后更新数据
  const handleCountdownEnd = (itemId: string) => {
    if (!isMounted) return;
    // if (swiperInstance) {
    //   swiperInstance.autoplay.stop();
    // }
    // const currentClicks = clicks();
    // const newClicks = [...currentClicks, Date.now()]; // 记录点击时间或序号
    // setClicks(newClicks);
    //
    // newClicks.forEach((_, index) => {
    //   setTimeout(() => {
    //     let list = dataList();
    //     if (list.length > 0 && list.filter(item => item.id === itemId && item.status)?.length > 0) {
    //       list = list
    //         .map((item) => {
    //           if (item.id === itemId) item.status = false;
    //           return {
    //             ...item,
    //           };
    //         })
    //         .filter((item) => item.status);
    //       setDataList(list);
    //       setSessionStorageItem(SessionStorageKey.Buoy, list);
    //     }
    //   }, index * 3000); // 每 5 秒执行一次
    // });

    let list = dataList();
    if (
      list.length > 0 &&
      list.filter((item) => item.id === itemId && item.status)?.length > 0
    ) {
      destroySwiper();
      list = list.map((item) => {
        if (item.id === itemId) item.manualStatus = false;
        return {
          ...item,
        };
      });
      const temp = getSessionStorageItem(SessionStorageKey.Buoy, []);
      setSessionStorageItem(
        SessionStorageKey.Buoy,
        temp.map((item: BuoyItem) => {
          if (item.id === itemId) item.manualStatus = false;
          return {
            ...item,
          };
        }),
      );
      setDataList(list.filter((item) => item.status && item.manualStatus));
    }
  };

  // const handleClose = (itemId: string) => {
  //
  // }

  onMount(() => {
    isMounted = true;
    window.addEventListener("scroll", handleScroll);

    onCleanup(() => {
      isMounted = false;
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    });
  });

  const slideItemStyle = (item: BuoyItem) => (
    <SwiperSlide
      item={item}
      class="flex h-full w-full cursor-pointer items-center justify-center pt-[16px]"
    >
      <div
        class={cn(
          "relative flex flex-col pr-[16px]",
          isMobile() ? "h-[130px] w-[111px]" : "h-[170px] w-[151px]",
        )}
      >
        <div class={isMobile() ? "h-[120px]" : "h-[160px]"}>
          <img
            src={item?.icon?.[getLangKey()]}
            class={cn(
              isMobile() ? "h-[100px] w-[100px]" : "h-[140px] w-[140px]",
            )}
            // onError={(e) => {
            //   e.target.onerror = null;
            //   e.target.src = item?.icon?.[LocaleMap.en];
            // }}
            alt={getImageAltByLang("userProfileImg")}
          />
          <div
            style={{
              "margin-right": isMobile() ? "-10px" : "-22px",
              "margin-top": isMobile() ? "-9px" : "-9px",
              position: "absolute",
              width: "30%",
              height: "50%",
              right: "0",
              top: "0",
            }}
          >
            <Icon
              name="general/xx3_activity"
              color="var(--general-white)"
              class="w-[16px] cursor-pointer"
              onClick={() => {
                handleCountdownEnd(item.id);
              }}
            />
          </div>
        </div>
        <Show when={item?.countdown_status} fallback={<></>}>
          <CountdownButton
            item={item}
            onCountdownEnd={() => handleCountdownEnd(item.id)}
          />
        </Show>
      </div>
    </SwiperSlide>
  );

  return (
    <div
      class={cn(
        "z-1000 fixed top-3/4",
        isMobile()
          ? "top-13/20 h-[136px] w-[111px]"
          : "top-3/4 h-[176px] w-[151px]",
        visible() ? "visible opacity-100" : "invisible opacity-0",
      )}
      style={{ "transition-duration": ".2s", right: `${offsetRight()}px` }}
    >
      <SwiperBase
        dataList={dataList()}
        swiperRef={(el) => (swiperContainer = el)}
        slideItemStyle={slideItemStyle}
        class="w-full"
      />
    </div>
  );
};

export default BuoyComponent;
